import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { DesignGroup } from '@/modules/Orders/CreateOrders/StoreCheckout/types/storeReviewOrderStep.types';
import {
  getStoreReviewDefaultMinimumQuantity,
  getStoreReviewPricingBreakpoints,
  resolveStoreReviewUnifiedCartMinimum,
} from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeReviewPricing.utils';
import {
  applyQuotedItemsToDesignGroups,
  areStoreReviewItemsQuotedAtQuantity,
  areStoreReviewItemsQuotedAtTheirMinimums,
  buildStoreReviewItemsQuoteStateKey,
  buildStoreReviewQuoteRequestKey,
  quoteStoreReviewDesignGroupItems,
  quoteStoreReviewDesignGroupItemsAtTheirMinimums,
} from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeReviewQuote.utils';
import {
  isStoreCartDesignGroupMoqPending,
  shouldShowStoreCartDesignGroupMoqTiers,
} from '@/modules/Cart/StoreCart/helpers/storeCartSetup.utils';
import type { DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS } from '@/modules/Cart/StoreCart/storeCart.constants';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import { useGetQuoteMutation } from '@/api/QuoterQuery';
import { getStoreCartDesignGroupMoqRequestKey } from '@/modules/Cart/StoreCart/helpers/storeCartApiSync.utils';
import type { StoreCartMoqShippingLane } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';

interface IUseStoreReviewDesignGroupQuotesParams {
  designGroups: DesignGroup[];
  selectedMinimums: Record<number, number>;
  allItems: ICartItemDetail[];
  isPricingReadOnly: boolean;
  moqShippingLane: StoreCartMoqShippingLane;
  designGroupMoqFetchRequestKey: string;
  isDesignGroupMoqLoading: boolean;
  getDesignGroupMoq: (group: DesignGroup) => number | undefined;
  getDesignGroupMoqApiStatus: (
    group: DesignGroup
  ) => typeof DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS;
  onQuotedItemsChange: (nextItems: ICartItemDetail[]) => void;
  setDesignGroups: Dispatch<SetStateAction<DesignGroup[]>>;
  onShippingLaneRequoteComplete?: (quotedItems: ICartItemDetail[]) => void;
}

const isDesignGroupQuotedForShippingLane = (
  groupIndex: number,
  group: DesignGroup,
  moqShippingLane: StoreCartMoqShippingLane,
  lastQuotedRequestKeyByGroupIndex: Record<number, string>,
  getDesignGroupMoqApiStatus: (
    group: DesignGroup
  ) => typeof DEFAULT_STORE_CART_DESIGN_GROUP_API_STATUS
): boolean => {
  if (group.items.every((item) => typeof item.designId !== 'number')) {
    return true;
  }

  const apiStatus = getDesignGroupMoqApiStatus(group);
  const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(apiStatus);

  if (hasMoqTiers && isStoreCartDesignGroupMoqPending(apiStatus)) {
    return false;
  }

  const requestKey = lastQuotedRequestKeyByGroupIndex[groupIndex];

  return Boolean(requestKey?.startsWith(`${moqShippingLane}:`));
};

export const useStoreReviewDesignGroupQuotes = ({
  designGroups,
  selectedMinimums,
  allItems,
  isPricingReadOnly,
  moqShippingLane,
  designGroupMoqFetchRequestKey,
  isDesignGroupMoqLoading,
  getDesignGroupMoq,
  getDesignGroupMoqApiStatus,
  onQuotedItemsChange,
  setDesignGroups,
  onShippingLaneRequoteComplete,
}: IUseStoreReviewDesignGroupQuotesParams): {
  markUserSelectedGroupMinimum: (groupIndex: number, quantity: number) => void;
  clearUserSelectedGroupMinimum: (groupIndex: number) => void;
} => {
  const [getQuote] = useGetQuoteMutation();
  const quoteRequestIdRef = useRef(0);
  const lastQuotedRequestKeyRef = useRef<Record<number, string>>({});
  const userSelectedGroupMinimumRef = useRef<Record<number, number>>({});
  const onQuotedItemsChangeRef = useRef(onQuotedItemsChange);
  const onShippingLaneRequoteCompleteRef = useRef(onShippingLaneRequoteComplete);
  const previousMoqShippingLaneRef = useRef(moqShippingLane);
  const pendingShippingLaneRequoteRef = useRef<StoreCartMoqShippingLane | null>(null);
  const shippingLaneQuotedItemsByIdRef = useRef<Record<number, ICartItemDetail>>({});
  const allItemsRef = useRef(allItems);
  allItemsRef.current = allItems;

  const markUserSelectedGroupMinimum = useCallback((groupIndex: number, quantity: number) => {
    userSelectedGroupMinimumRef.current[groupIndex] = quantity;
  }, []);

  const clearUserSelectedGroupMinimum = useCallback((groupIndex: number) => {
    delete userSelectedGroupMinimumRef.current[groupIndex];
  }, []);

  useEffect(() => {
    onQuotedItemsChangeRef.current = onQuotedItemsChange;
  }, [onQuotedItemsChange]);

  useEffect(() => {
    onShippingLaneRequoteCompleteRef.current = onShippingLaneRequoteComplete;
  }, [onShippingLaneRequoteComplete]);

  useEffect(() => {
    if (previousMoqShippingLaneRef.current === moqShippingLane) {
      return;
    }

    previousMoqShippingLaneRef.current = moqShippingLane;
    lastQuotedRequestKeyRef.current = {};
    shippingLaneQuotedItemsByIdRef.current = {};
    pendingShippingLaneRequoteRef.current = moqShippingLane;
  }, [moqShippingLane]);

  const tryCompleteShippingLaneRequote = useCallback(() => {
    if (pendingShippingLaneRequoteRef.current !== moqShippingLane) {
      return;
    }

    if (isPricingReadOnly || designGroups.length === 0) {
      pendingShippingLaneRequoteRef.current = null;
      shippingLaneQuotedItemsByIdRef.current = {};
      onShippingLaneRequoteCompleteRef.current?.([]);
      return;
    }

    const allSettled = designGroups.every((group, groupIndex) =>
      isDesignGroupQuotedForShippingLane(
        groupIndex,
        group,
        moqShippingLane,
        lastQuotedRequestKeyRef.current,
        getDesignGroupMoqApiStatus
      )
    );

    if (!allSettled) {
      return;
    }

    const quotedItems = allItemsRef.current.map(
      (item) => shippingLaneQuotedItemsByIdRef.current[item.id] ?? item
    );

    pendingShippingLaneRequoteRef.current = null;
    shippingLaneQuotedItemsByIdRef.current = {};
    onQuotedItemsChangeRef.current?.(quotedItems);
    onShippingLaneRequoteCompleteRef.current?.(quotedItems);
  }, [designGroups, getDesignGroupMoqApiStatus, isPricingReadOnly, moqShippingLane]);

  const applyQuotedGroupItems = useCallback(
    (groupIndex: number, quotedItems: ICartItemDetail[]) => {
      const isShippingLaneRequote = pendingShippingLaneRequoteRef.current === moqShippingLane;

      if (isShippingLaneRequote) {
        quotedItems.forEach((item) => {
          shippingLaneQuotedItemsByIdRef.current[item.id] = item;
        });
      }

      setDesignGroups((prevGroups) => {
        const nextGroups = applyQuotedItemsToDesignGroups(prevGroups, groupIndex, quotedItems);

        if (!isShippingLaneRequote) {
          onQuotedItemsChangeRef.current?.(nextGroups.flatMap((nextGroup) => nextGroup.items));
        }

        return nextGroups;
      });
    },
    [moqShippingLane, setDesignGroups]
  );

  const buildQuoteOptions = useCallback(
    (
      forceRequote: boolean
    ): { storeShippingLane: StoreCartMoqShippingLane; forceRequote?: boolean } => ({
      storeShippingLane: moqShippingLane,
      ...(forceRequote ? { forceRequote: true } : {}),
    }),
    [moqShippingLane]
  );

  const quoteDesignGroupAtQuantity = useCallback(
    async (groupIndex: number, quantity: number, forceRequote: boolean): Promise<void> => {
      const group = designGroups[groupIndex];

      if (!group || !Number.isFinite(quantity) || quantity <= 0) {
        return;
      }

      const requestKey = `${moqShippingLane}:${buildStoreReviewQuoteRequestKey(
        groupIndex,
        quantity,
        group
      )}`;
      const requestId = quoteRequestIdRef.current + 1;
      quoteRequestIdRef.current = requestId;

      const quotedItems = await quoteStoreReviewDesignGroupItems(
        group,
        allItems,
        quantity,
        getQuote,
        buildQuoteOptions(forceRequote)
      );

      if (quoteRequestIdRef.current !== requestId) {
        return;
      }

      lastQuotedRequestKeyRef.current[groupIndex] = requestKey;
      applyQuotedGroupItems(groupIndex, quotedItems);
    },
    [allItems, applyQuotedGroupItems, buildQuoteOptions, designGroups, getQuote, moqShippingLane]
  );

  const quoteDesignGroupAtItemMinimums = useCallback(
    async (groupIndex: number, forceRequote: boolean): Promise<void> => {
      const group = designGroups[groupIndex];

      if (!group) {
        return;
      }

      const requestKey = `${moqShippingLane}:item-minimums:${buildStoreReviewItemsQuoteStateKey(
        group
      )}`;
      const requestId = quoteRequestIdRef.current + 1;
      quoteRequestIdRef.current = requestId;

      const quotedItems = await quoteStoreReviewDesignGroupItemsAtTheirMinimums(
        group,
        allItems,
        getQuote,
        buildQuoteOptions(forceRequote)
      );

      if (quoteRequestIdRef.current !== requestId) {
        return;
      }

      lastQuotedRequestKeyRef.current[groupIndex] = requestKey;
      applyQuotedGroupItems(groupIndex, quotedItems);
    },
    [allItems, applyQuotedGroupItems, buildQuoteOptions, designGroups, getQuote, moqShippingLane]
  );

  useEffect(() => {
    if (isPricingReadOnly || designGroups.length === 0) {
      tryCompleteShippingLaneRequote();
      return;
    }

    const shouldForceShippingLaneRequote =
      pendingShippingLaneRequoteRef.current === moqShippingLane;

    const expectedMoqRequestKey = getStoreCartDesignGroupMoqRequestKey(allItems, moqShippingLane);

    if (
      shouldForceShippingLaneRequote &&
      expectedMoqRequestKey &&
      (isDesignGroupMoqLoading || designGroupMoqFetchRequestKey !== expectedMoqRequestKey)
    ) {
      return;
    }

    designGroups.forEach((group, groupIndex) => {
      const apiStatus = getDesignGroupMoqApiStatus(group);
      const designGroupMoq = getDesignGroupMoq(group);
      const hasMoqTiers = shouldShowStoreCartDesignGroupMoqTiers(apiStatus);

      if (hasMoqTiers && isStoreCartDesignGroupMoqPending(apiStatus)) {
        return;
      }

      const { quantities } = getStoreReviewPricingBreakpoints(
        group,
        hasMoqTiers ? designGroupMoq : undefined
      );
      const defaultMinimum = getStoreReviewDefaultMinimumQuantity(group, quantities);
      const unifiedCartMinimum = resolveStoreReviewUnifiedCartMinimum(group, quantities);
      const selectedMinimum = selectedMinimums[groupIndex] ?? defaultMinimum;
      const userChangedGroupMinimum = userSelectedGroupMinimumRef.current[groupIndex] != null;
      const hasSelectedGroupMinimum = selectedMinimums[groupIndex] != null;
      const shouldQuoteAtGroupMinimum =
        userChangedGroupMinimum ||
        hasSelectedGroupMinimum ||
        unifiedCartMinimum != null ||
        (shouldForceShippingLaneRequote && hasMoqTiers && quantities.includes(selectedMinimum));

      if (shouldQuoteAtGroupMinimum) {
        const requestKey = `${moqShippingLane}:${buildStoreReviewQuoteRequestKey(
          groupIndex,
          selectedMinimum,
          group
        )}`;

        if (lastQuotedRequestKeyRef.current[groupIndex] === requestKey) {
          return;
        }

        if (
          !shouldForceShippingLaneRequote &&
          areStoreReviewItemsQuotedAtQuantity(group.items, selectedMinimum)
        ) {
          lastQuotedRequestKeyRef.current[groupIndex] = requestKey;
          return;
        }

        quoteDesignGroupAtQuantity(groupIndex, selectedMinimum, shouldForceShippingLaneRequote)
          .catch(() => undefined)
          .finally(() => {
            tryCompleteShippingLaneRequote();
          });
        return;
      }

      const requestKey = `${moqShippingLane}:item-minimums:${buildStoreReviewItemsQuoteStateKey(
        group
      )}`;

      if (lastQuotedRequestKeyRef.current[groupIndex] === requestKey) {
        return;
      }

      if (
        !shouldForceShippingLaneRequote &&
        areStoreReviewItemsQuotedAtTheirMinimums(group.items)
      ) {
        lastQuotedRequestKeyRef.current[groupIndex] = requestKey;
        return;
      }

      quoteDesignGroupAtItemMinimums(groupIndex, shouldForceShippingLaneRequote)
        .catch(() => undefined)
        .finally(() => {
          tryCompleteShippingLaneRequote();
        });
    });

    tryCompleteShippingLaneRequote();
  }, [
    allItems,
    designGroupMoqFetchRequestKey,
    designGroups,
    getDesignGroupMoq,
    getDesignGroupMoqApiStatus,
    isDesignGroupMoqLoading,
    isPricingReadOnly,
    moqShippingLane,
    quoteDesignGroupAtItemMinimums,
    quoteDesignGroupAtQuantity,
    selectedMinimums,
    tryCompleteShippingLaneRequote,
  ]);

  return { markUserSelectedGroupMinimum, clearUserSelectedGroupMinimum };
};
